import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../configureStore";
import { ConfigDetail, ConfigListItem } from "../interface/configs";
import { FormConfig } from "../components/organisms/TaxReturnWebforms/types/formConfig";
import {
    GET_CONFIGS_REQUEST,
    GET_CONFIGS_SUCCESS,
    GET_CONFIG_CONTENT_REQUEST,
    GET_CONFIG_CONTENT_SUCCESS,
    GET_CONFIG_CONTENT_FAILURE,
    UPDATE_CONFIG_REQUEST,
    UPDATE_CONFIG_SUCCESS,
    getConfigs as remoteGetConfigs,
    getConfigContent as remoteGetConfigContent,
    updateConfig as remoteUpdateConfig,
} from "../redux/reducer/configs";
import { Payload } from "../redux/reducer";
import LOCAL_DEV_CONFIGS, { USE_LOCAL_CONFIGS } from "./localDevConfigs";

// Hook-based sibling of useFormConfig for the Config Editor. Owns the
// USE_LOCAL_CONFIGS branch in the component tree (refresh-safe) so the reducer
// never needs to import localDevConfigs — which keeps JSON edits off the
// reducer's dep graph and preserves React Fast Refresh.
export function useConfigEditorActions() {
    const dispatch = useDispatch<AppDispatch>();
    const selectedConfigName = useSelector<RootState, string | null>(
        ({ configs }) => configs.selectedConfig?.name ?? null,
    );

    const fetchList = useCallback((payload: Payload<any> = {}) => {
        if (!USE_LOCAL_CONFIGS) {
            dispatch(remoteGetConfigs(payload));
            return;
        }
        dispatch({ type: GET_CONFIGS_REQUEST });
        const configs: ConfigListItem[] = Object.keys(LOCAL_DEV_CONFIGS).map((key) => ({
            name: `configs/${key}.json`,
            displayName: `${key}.json`,
            lastModified: new Date().toISOString(),
            contentLength: JSON.stringify(LOCAL_DEV_CONFIGS[key]).length,
        }));
        dispatch({ type: GET_CONFIGS_SUCCESS, data: { configs, count: configs.length } });
        payload.onSuccess?.(configs);
    }, [dispatch]);

    const fetchContent = useCallback((payload: Payload<{ configName: string }>) => {
        if (!USE_LOCAL_CONFIGS) {
            dispatch(remoteGetConfigContent(payload));
            return;
        }
        dispatch({ type: GET_CONFIG_CONTENT_REQUEST });
        const key = payload.input?.configName || "";
        const localConfig = LOCAL_DEV_CONFIGS[key];
        if (localConfig) {
            const detail: ConfigDetail = {
                name: `configs/${key}.json`,
                content: localConfig,
                lastModified: new Date().toISOString(),
                contentLength: JSON.stringify(localConfig).length,
                versionId: "local-dev",
            };
            dispatch({ type: GET_CONFIG_CONTENT_SUCCESS, data: detail });
            payload.onSuccess?.(detail);
        } else {
            dispatch({ type: GET_CONFIG_CONTENT_FAILURE });
        }
    }, [dispatch]);

    const save = useCallback((payload: Payload<{ configName: string; content: FormConfig }>) => {
        if (!USE_LOCAL_CONFIGS) {
            dispatch(remoteUpdateConfig(payload));
            return;
        }
        dispatch({ type: UPDATE_CONFIG_REQUEST });
        const content = payload.input?.content as FormConfig;
        const name = payload.input?.configName || "";
        LOCAL_DEV_CONFIGS[name] = content;
        dispatch({
            type: UPDATE_CONFIG_SUCCESS,
            data: { success: true, name, versionId: "local-dev" },
            content,
        });
        payload.onSuccess?.({ success: true, name, versionId: "local-dev" });
    }, [dispatch]);

    // When a JSON file is edited on disk, Fast Refresh hot-patches this hook
    // with the new LOCAL_DEV_CONFIGS values but Redux's selectedConfig still
    // holds the pre-save snapshot. Re-dispatch fetchContent for the currently
    // selected config on every render in local mode — it's a cheap in-memory
    // read and the reducer short-circuits to the same state if nothing changed.
    const refetchSelected = useCallback(() => {
        if (!USE_LOCAL_CONFIGS || !selectedConfigName) return;
        const key = selectedConfigName.replace("configs/", "").replace(".json", "");
        fetchContent({ input: { configName: key } });
    }, [selectedConfigName, fetchContent]);

    return { fetchList, fetchContent, save, refetchSelected };
}
